import React, { useState, useEffect } from 'react';
import { Upload, FileText, Bell, Camera, AlertCircle, LogOut, Users, MessageSquare, Eye, ArrowLeft, Download, CheckCircle, Clock, Trash2, Edit, Menu, X, Plus, Image as ImageIcon } from 'lucide-react';

// Firebase imports
import { initializeApp } from 'firebase/app';
import { getAuth, signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, addDoc, getDocs, query, where, orderBy, doc, serverTimestamp, updateDoc, deleteDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyB24dkSnqOSnMIZW9DpAZ9HpeO_KcqdxtY",
  authDomain: "cblue-app.firebaseapp.com",
  projectId: "cblue-app",
  storageBucket: "cblue-app.firebasestorage.app",
  messagingSenderId: "605890117013",
  appId: "1:605890117013:web:e167217ef35e5295f7ca9c"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);

// IMPORTANT: Set your admin email here
const ADMIN_EMAIL = 'erinsaylor30@gmail.com';

// EmailJS Configuration - REPLACE WITH YOUR CREDENTIALS FROM EMAILJS.COM
const EMAILJS_CONFIG = {
  serviceId: 'service_9g0tpw9',
  templateId: 'template_4as4tkw',
  publicKey: 'vOADwlDbz4GjwDf9q'
};

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [editingRequest, setEditingRequest] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  
  // Data states
  const [properties, setProperties] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [workRequests, setWorkRequests] = useState([]);
  const [observations, setObservations] = useState([]);
  const [reports, setReports] = useState([]);
  const [propertyPhotos, setPropertyPhotos] = useState([]);
const [resources, setResources] = useState([]);

  // Form states
  const [updateForm, setUpdateForm] = useState({ propertyId: '', title: '', content: '', pdfFile: null, imageFile: null });
  const [workRequestForm, setWorkRequestForm] = useState({ propertyId: '', urgency: 'Medium', area: '', description: '', timeline: '' });
  const [observationForm, setObservationForm] = useState({ propertyId: '', species: '', location: '', notes: '', photo: null });
  const [completionForm, setCompletionForm] = useState({ summary: '', completionDate: '' });
  const [photoUploadForm, setPhotoUploadForm] = useState({ propertyId: '', caption: '', photo: null, isCover: false });
  const [resourceForm, setResourceForm] = useState({ title: '', url: '', description: '', imageFile: null });
// Check authentication state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        const isAdmin = user.email === ADMIN_EMAIL;
        setUserRole(isAdmin ? 'admin' : 'client');
        setActiveView(isAdmin ? 'dashboard' : 'properties');
        await loadData(user, isAdmin);
      } else {
        setCurrentUser(null);
        setUserRole(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const loadData = async (user, isAdmin) => {
    try {
      if (isAdmin) {
        const propsSnap = await getDocs(collection(db, 'properties'));
        setProperties(propsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        
        const updatesSnap = await getDocs(query(collection(db, 'updates'), orderBy('createdAt', 'desc')));
        setUpdates(updatesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        
        const requestsSnap = await getDocs(collection(db, 'workRequests'));
        setWorkRequests(requestsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        
        const obsSnap = await getDocs(collection(db, 'observations'));
        setObservations(obsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

        const reportsSnap = await getDocs(collection(db, 'reports'));
        setReports(reportsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

        const photosSnap = await getDocs(collection(db, 'propertyPhotos'));
        setPropertyPhotos(photosSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
const resourcesSnap = await getDocs(collection(db, 'resources'));
      setResources(resourcesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } else {
        const propsSnap = await getDocs(query(collection(db, 'properties'), where('userId', '==', user.uid)));
        setProperties(propsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        
        const propertyIds = propsSnap.docs.map(doc => doc.id);
        
        if (propertyIds.length > 0) {
          const updatesSnap = await getDocs(collection(db, 'updates'));
          const filteredUpdates = updatesSnap.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(u => u.propertyId === 'all' || propertyIds.includes(u.propertyId));
          setUpdates(filteredUpdates);
          
          const obsSnap = await getDocs(query(collection(db, 'observations'), where('userId', '==', user.uid)));
          setObservations(obsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

          const requestsSnap = await getDocs(query(collection(db, 'workRequests'), where('userId', '==', user.uid)));
          setWorkRequests(requestsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

          const reportsSnap = await getDocs(collection(db, 'reports'));
          const filteredReports = reportsSnap.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(r => propertyIds.includes(r.propertyId));
          setReports(filteredReports);

          const photosSnap = await getDocs(collection(db, 'propertyPhotos'));
          const filteredPhotos = photosSnap.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(p => propertyIds.includes(p.propertyId));
          setPropertyPhotos(filteredPhotos);
          const resourcesSnap = await getDocs(collection(db, 'resources'));
          setResources(resourcesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }
      }
    } catch (err) {
      console.error('Error loading data:', err);
    }
  };

  const sendEmailNotification = async (type, data) => {
    try {
      const emailData = {
        service_id: EMAILJS_CONFIG.serviceId,
        template_id: EMAILJS_CONFIG.templateId,
        user_id: EMAILJS_CONFIG.publicKey,
        template_params: {
          to_email: ADMIN_EMAIL,
          notification_type: type,
          user_email: data.userEmail || currentUser.email,
          property_name: data.propertyName || '',
          details: data.details || '',
          urgency: data.urgency || '',
          area: data.area || '',
          species: data.species || '',
          location: data.location || ''
        }
      };

      await fetch('https://api.emailjs.com/api/v1.0/email/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailData)
      });
      console.log('Email notification sent');
    } catch (error) {
      console.error('Email notification error:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      await signInWithEmailAndPassword(auth, email, password);
      setEmail('');
      setPassword('');
    } catch (err) {
      setError('Invalid email or password');
      console.error('Login error:', err);
    }
    setLoading(false);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setActiveView('dashboard');
      setMobileMenuOpen(false);
    } catch (err) {
      console.error('Logout error:', err);
    }
  };
const handlePostUpdate = async (e) => {
    e.preventDefault();
    try {
      let pdfURL = null;
      let imageURL = null;

      if (updateForm.pdfFile) {
        const pdfRef = ref(storage, `updates/${Date.now()}_${updateForm.pdfFile.name}`);
        await uploadBytes(pdfRef, updateForm.pdfFile);
        pdfURL = await getDownloadURL(pdfRef);
      }

      if (updateForm.imageFile) {
        const imageRef = ref(storage, `updates/${Date.now()}_${updateForm.imageFile.name}`);
        await uploadBytes(imageRef, updateForm.imageFile);
        imageURL = await getDownloadURL(imageRef);
      }

      await addDoc(collection(db, 'updates'), {
        propertyId: updateForm.propertyId,
        title: updateForm.title,
        content: updateForm.content,
        pdfURL: pdfURL,
        pdfName: updateForm.pdfFile ? updateForm.pdfFile.name : null,
        imageURL: imageURL,
        imageName: updateForm.imageFile ? updateForm.imageFile.name : null,
        createdAt: serverTimestamp(),
        createdBy: currentUser.uid
      });
      setUpdateForm({ propertyId: '', title: '', content: '', pdfFile: null, imageFile: null });
      alert('Update posted successfully!');
      await loadData(currentUser, userRole === 'admin');
    } catch (err) {
      console.error('Error posting update:', err);
      alert('Error posting update. Please try again.');
    }
  };

  const handleSubmitWorkRequest = async (e) => {
    e.preventDefault();
    try {
      const property = properties.find(p => p.id === workRequestForm.propertyId);
      await addDoc(collection(db, 'workRequests'), {
        propertyId: workRequestForm.propertyId,
        propertyName: property?.name,
        userId: currentUser.uid,
        userEmail: currentUser.email,
        urgency: workRequestForm.urgency,
        area: workRequestForm.area,
        description: workRequestForm.description,
        timeline: workRequestForm.timeline,
        status: 'pending',
        createdAt: serverTimestamp()
      });

      // Send email notification
      await sendEmailNotification('Work Request', {
        userEmail: currentUser.email,
        propertyName: property?.name,
        urgency: workRequestForm.urgency,
        area: workRequestForm.area,
        details: workRequestForm.description
      });

      setWorkRequestForm({ propertyId: '', urgency: 'Medium', area: '', description: '', timeline: '' });
      alert('Work request submitted successfully!');
      await loadData(currentUser, userRole === 'admin');
    } catch (err) {
      console.error('Error submitting work request:', err);
      alert('Error submitting request. Please try again.');
    }
  };

  const handleUpdateWorkRequestStatus = async (requestId, newStatus) => {
    try {
      const requestRef = doc(db, 'workRequests', requestId);
      await updateDoc(requestRef, {
        status: newStatus,
        updatedAt: serverTimestamp()
      });
      alert(`Work request marked as ${newStatus}!`);
      await loadData(currentUser, userRole === 'admin');
    } catch (err) {
      console.error('Error updating work request:', err);
      alert('Error updating status. Please try again.');
    }
  };

  const handleSaveCompletionSummary = async (requestId) => {
    try {
      const requestRef = doc(db, 'workRequests', requestId);
      await updateDoc(requestRef, {
        status: 'completed',
        completionSummary: completionForm.summary,
        completionDate: completionForm.completionDate,
        updatedAt: serverTimestamp()
      });
      setEditingRequest(null);
      setCompletionForm({ summary: '', completionDate: '' });
      alert('Completion details saved!');
      await loadData(currentUser, userRole === 'admin');
    } catch (err) {
      console.error('Error saving completion summary:', err);
      alert('Error saving. Please try again.');
    }
  };

  const handleDeleteObservation = async (observationId) => {
    if (window.confirm('Are you sure you want to delete this observation? This cannot be undone.')) {
      try {
        await deleteDoc(doc(db, 'observations', observationId));
        alert('Observation deleted successfully!');
        await loadData(currentUser, userRole === 'admin');
      } catch (err) {
        console.error('Error deleting observation:', err);
        alert('Error deleting observation. Please try again.');
      }
    }
  };

  const handleSubmitObservation = async (e) => {
    e.preventDefault();
    try {
      const property = properties.find(p => p.id === observationForm.propertyId);
      
      let photoURL = null;
      if (observationForm.photo) {
        const photoRef = ref(storage, `observations/${currentUser.uid}/${Date.now()}_${observationForm.photo.name}`);
        await uploadBytes(photoRef, observationForm.photo);
        photoURL = await getDownloadURL(photoRef);
      }
      
      await addDoc(collection(db, 'observations'), {
        propertyId: observationForm.propertyId,
        propertyName: property?.name,
        userId: currentUser.uid,
        userEmail: currentUser.email,
        species: observationForm.species,
        location: observationForm.location,
        notes: observationForm.notes,
        photoURL: photoURL,
        createdAt: serverTimestamp()
      });

      // Send email notification
      await sendEmailNotification('Observation', {
        userEmail: currentUser.email,
        propertyName: property?.name,
        species: observationForm.species,
        location: observationForm.location,
        details: observationForm.notes
      });
      
      setObservationForm({ propertyId: '', species: '', location: '', notes: '', photo: null });
      alert('Observation submitted successfully!');
      await loadData(currentUser, userRole === 'admin');
      if (selectedProperty) {
        setActiveView('propertyDetail');
      } else {
        setActiveView('observations');
      }
    } catch (err) {
      console.error('Error submitting observation:', err);
      alert('Error submitting observation. Please try again.');
    }
  };

  const handleUploadPropertyPhoto = async (e) => {
    e.preventDefault();
    try {
      if (!photoUploadForm.photo) {
        alert('Please select a photo');
        return;
      }

      const photoRef = ref(storage, `propertyPhotos/${photoUploadForm.propertyId}/${Date.now()}_${photoUploadForm.photo.name}`);
      await uploadBytes(photoRef, photoUploadForm.photo);
      const photoURL = await getDownloadURL(photoRef);

      // If setting as cover, unset other cover photos
      if (photoUploadForm.isCover) {
        const existingPhotos = propertyPhotos.filter(p => p.propertyId === photoUploadForm.propertyId);
        for (const photo of existingPhotos) {
          if (photo.isCover) {
            await updateDoc(doc(db, 'propertyPhotos', photo.id), { isCover: false });
          }
        }
      }

      await addDoc(collection(db, 'propertyPhotos'), {
        propertyId: photoUploadForm.propertyId,
        photoURL: photoURL,
        caption: photoUploadForm.caption,
        isCover: photoUploadForm.isCover,
        createdAt: serverTimestamp(),
        createdBy: currentUser.uid
      });

      setPhotoUploadForm({ propertyId: '', caption: '', photo: null, isCover: false });
      alert('Photo uploaded successfully!');
      await loadData(currentUser, userRole === 'admin');
    } catch (err) {
      console.error('Error uploading photo:', err);
      alert('Error uploading photo. Please try again.');
    }
  };

  const handleDeletePropertyPhoto = async (photoId, photoURL) => {
    if (window.confirm('Are you sure you want to delete this photo?')) {
      try {
        const photoRef = ref(storage, photoURL);
        await deleteObject(photoRef);
        await deleteDoc(doc(db, 'propertyPhotos', photoId));
        alert('Photo deleted successfully!');
        await loadData(currentUser, userRole === 'admin');
      } catch (err) {
        console.error('Error deleting photo:', err);
        alert('Error deleting photo. Please try again.');
      }
    }
  };

  const handleSetCoverPhoto = async (photoId) => {
    try {
      const photo = propertyPhotos.find(p => p.id === photoId);
      if (!photo) return;

      const propertyPhotosForProperty = propertyPhotos.filter(p => p.propertyId === photo.propertyId);
      for (const p of propertyPhotosForProperty) {
        if (p.isCover) {
          await updateDoc(doc(db, 'propertyPhotos', p.id), { isCover: false });
        }
      }

      await updateDoc(doc(db, 'propertyPhotos', photoId), { isCover: true });
      alert('Cover photo updated!');
      await loadData(currentUser, userRole === 'admin');
    } catch (err) {
      console.error('Error setting cover photo:', err);
      alert('Error updating cover photo. Please try again.');
    }
  };

  const getCoverPhoto = (propertyId) => {
    const coverPhoto = propertyPhotos.find(p => p.propertyId === propertyId && p.isCover);
    return coverPhoto?.photoURL || null;
  };

  const getPropertyPhotos = (propertyId) => {
    return propertyPhotos.filter(p => p.propertyId === propertyId);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setObservationForm({...observationForm, photo: file});
    }
  };

  const handlePDFChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUpdateForm({...updateForm, pdfFile: file});
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUpdateForm({...updateForm, imageFile: file});
    }
  };

  const handlePhotoUploadChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoUploadForm({...photoUploadForm, photo: file});
    }
  };
const handleResourceImageChange = (e) => {
  const file = e.target.files[0];
  if (file) {
    setResourceForm({...resourceForm, imageFile: file});
  }
};
const handleSubmitResource = async (e) => {
    e.preventDefault();
    try {
      let imageURL = null;

      if (resourceForm.imageFile) {
        const imageRef = ref(storage, `resources/${Date.now()}_${resourceForm.imageFile.name}`);
        await uploadBytes(imageRef, resourceForm.imageFile);
        imageURL = await getDownloadURL(imageRef);
      }

      await addDoc(collection(db, 'resources'), {
        title: resourceForm.title,
        url: resourceForm.url,
        description: resourceForm.description,
        imageURL: imageURL,
        createdAt: serverTimestamp(),
        createdBy: currentUser.uid
      });

      setResourceForm({ title: '', url: '', description: '', imageFile: null });
      alert('Resource added successfully!');
      await loadData(currentUser, userRole === 'admin');
    } catch (err) {
      console.error('Error adding resource:', err);
      alert('Error adding resource. Please try again.');
    }
  };

  const handleDeleteResource = async (resourceId, imageURL) => {
    if (window.confirm('Are you sure you want to delete this resource?')) {
      try {
        if (imageURL) {
          const imageRef = ref(storage, imageURL);
          await deleteObject(imageRef);
        }
        await deleteDoc(doc(db, 'resources', resourceId));
        alert('Resource deleted successfully!');
        await loadData(currentUser, userRole === 'admin');
      } catch (err) {
        console.error('Error deleting resource:', err);
        alert('Error deleting resource. Please try again.');
      }
    }
  }; 
  const handleDeleteUpdate = async (updateId, pdfURL, imageURL) => {
    if (window.confirm('Are you sure you want to delete this update?')) {
      try {
        if (pdfURL) {
          const pdfRef = ref(storage, pdfURL);
          await deleteObject(pdfRef);
        }
        if (imageURL) {
          const imageRef = ref(storage, imageURL);
          await deleteObject(imageRef);
        }
        await deleteDoc(doc(db, 'updates', updateId));
        alert('Update deleted successfully!');
        await loadData(currentUser, userRole === 'admin');
      } catch (err) {
        console.error('Error deleting update:', err);
        alert('Error deleting update. Please try again.');
      }
    }
  };
  const handlePropertyClick = (property) => {
    setSelectedProperty(property);
    setActiveView('propertyDetail');
    setMobileMenuOpen(false);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Recent';
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleDateString();
    }
    return 'Recent';
  };

const navItems = userRole === 'admin' 
    ? [
        { id: 'dashboard', label: 'Dashboard' },
        { id: 'properties', label: 'Properties' },
        { id: 'updates', label: 'Post Updates' },
        { id: 'workRequests', label: 'Work Requests' },
        { id: 'observations', label: 'All Observations' },
        { id: 'resources', label: 'Resources' }
      ]
    : [
        { id: 'properties', label: 'My Properties' },
        { id: 'updates', label: 'Updates' },
        { id: 'resources', label: 'Resources' },
        { id: 'workRequest', label: 'Submit Request' }
      ];
if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center">
        <div className="text-center">
          <img src="/cblue-logo-small.png" alt="CBLUE" className="w-16 h-16 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Login Screen
  // Login Screen
  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Login Section with Background */}
        <div className="relative py-16">
          <div className="absolute inset-0 bg-cover bg-center" style={{backgroundImage: 'url(/background.jpg)'}}></div>
          <div className="absolute inset-0 bg-white opacity-50"></div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
              {/* Left Side - Welcome Message */}
              <div className="flex-1 text-gray-900">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
                  Welcome to CBLUE Portal
                </h1>
                <p className="text-xl sm:text-2xl text-gray-800">
                  Healthy Environments, for the People
                </p>
              </div>

              {/* Right Side - Login Form */}
              <div className="w-full max-w-md">
                <div className="bg-white rounded-lg shadow-xl p-6 sm:p-8">
                  <div className="text-center mb-8">
                    <div className="mb-6">
                      <img src="/cblue-logo.png" alt="CBLUE Logo" className="w-32 h-32 sm:w-40 sm:h-40 mx-auto" />
                    </div>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Client Login</h2>
                    <p className="text-sm sm:text-base text-gray-600 mt-2">Access your property portal</p>
                  </div>
                  
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                        required
                      />
                    </div>
                    
                    {error && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">
                        {error}
                      </div>
                    )}
                    
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-blue-600 text-white py-2 sm:py-3 rounded-md hover:bg-blue-700 transition disabled:opacity-50 text-base font-medium"
                    >
                      {loading ? 'Signing in...' : 'Sign In'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* About CBLUE Section */}
        <div className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">About CBLUE</h2>
              <div className="w-24 h-1 bg-blue-600 mx-auto"></div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  CBLUE is a professional land management service dedicated to maintaining and enhancing the ecological health of your property. With expertise in conservation, wildlife management, and sustainable land practices, we help property owners achieve their land stewardship goals.
                </p>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  Our approach combines traditional land management techniques with modern technology, providing clients with detailed documentation, regular updates, and transparent communication throughout every project.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Whether you own rural acreage, forestland, or agricultural property, CBLUE offers customized management plans that prioritize the long-term health and sustainability of your land.
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-6">
                {/* Replace these with your actual images */}
                <div className="bg-gray-100 rounded-lg overflow-hidden">
                  <img 
                    src="/about-image-1.jpg" 
                    alt="CBLUE work" 
                    className="w-full h-48 object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div class="w-full h-48 bg-gray-200 flex items-center justify-center text-gray-400 text-sm">Image 1<br/>Replace with your photo</div>';
                    }}
                  />
                </div>
                <div className="bg-gray-100 rounded-lg overflow-hidden">
                  <img 
                    src="/about-image-2.jpg" 
                    alt="CBLUE work" 
                    className="w-full h-48 object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div class="w-full h-48 bg-gray-200 flex items-center justify-center text-gray-400 text-sm">Image 2<br/>Replace with your photo</div>';
                    }}
                  />
                </div>
                <div className="bg-gray-100 rounded-lg overflow-hidden">
                  <img 
                    src="/about-image-3.jpg" 
                    alt="CBLUE work" 
                    className="w-full h-48 object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div class="w-full h-48 bg-gray-200 flex items-center justify-center text-gray-400 text-sm">Image 3<br/>Replace with your photo</div>';
                    }}
                  />
                </div>
                <div className="bg-gray-100 rounded-lg overflow-hidden">
                  <img 
                    src="/about-image-4.jpg" 
                    alt="CBLUE work" 
                    className="w-full h-48 object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div class="w-full h-48 bg-gray-200 flex items-center justify-center text-gray-400 text-sm">Image 4<br/>Replace with your photo</div>';
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Services & Pricing Section */}
        <div className="relative bg-gray-100 py-16">
          <div className="absolute inset-0 bg-cover bg-center" style={{backgroundImage: 'url(/services-background.jpg)'}}></div>
          <div className="absolute inset-0 bg-white opacity-50"></div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Services & Pricing</h2>
              <div className="w-24 h-1 bg-blue-600 mx-auto mb-6"></div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 max-w-4xl mx-auto">
              <div className="space-y-6">
                {/* CBLUE Membership */}
                <div className="border-b border-gray-200 pb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    CBLUE membership - <span className="text-blue-600">$35/month or $300/year</span>
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Description:</strong> Includes access to client portal, discounts on surveys, <strong>one free consultation yearly</strong>, and monthly newsletters. You DO NOT need to purchase surveys or be a property owner before becoming a member! Monthly membership can be cancelled at any time, but monthly charges are non-refundable once paid.
                  </p>
                </div>

                {/* Initial Property Survey */}
                <div className="border-b border-gray-200 pb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Initial property survey</h3>
                  <div className="space-y-2 mb-3">
                    <p className="text-gray-700">
                      <strong>(member price)</strong> - $125.00 + travel fee (0.35 cents per mile) + $15 for every acre over 1 acre <em>(capped at $400.00)</em>
                    </p>
                    <p className="text-gray-700">
                      <strong>(non-member price)</strong> - $155.00 + travel fee (0.35 cents per mile) + $15 for every acre over 1 acre <em>(capped at $430.00)</em>
                    </p>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Description:</strong> A private lands biologist will visit your property to conduct a thorough initial survey to generate a report including a species list of wildlife/plants currently on the property, essential property information (i.e. USDA soil types/testing, landscape-level attributes) habitat enhancement options/recommendations.
                  </p>
                </div>

                {/* Property Survey */}
                <div className="border-b border-gray-200 pb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Property survey <span className="text-sm text-gray-600">(only available AFTER initial survey is performed)</span>
                  </h3>
                  <div className="space-y-2 mb-3">
                    <p className="text-gray-700">
                      <strong>(member price)</strong> - $95.00 + travel fee (0.35 cents per mile) + $15 for every acre over 1 acre <em>(capped at $300.00)</em> + maintenance fees
                    </p>
                    <p className="text-gray-700">
                      <strong>(non-member price)</strong> - $125.00 + travel fee (0.35 cents per mile) + $15 for every acre over 1 acre <em>(capped at $330.00)</em> + maintenance fees
                    </p>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Description:</strong> A private lands biologist will visit your property to conduct a survey based on information from the initial survey and any prior management.
                  </p>
                </div>

                {/* Consultation */}
                <div className="border-b border-gray-200 pb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Consultation <span className="text-sm text-gray-600">(member and non-member price)</span> - <span className="text-blue-600">$50.00</span> + travel fee (0.35 cents per mile)
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Description:</strong> A private lands biologist will call you or visit your property to answer questions and provide general expertise on property management.
                  </p>
                </div>

                {/* Property Enhancements */}
                <div>
                  <p className="text-gray-700 leading-relaxed">
                    The prices of property enhancements such as bird boxes, bat houses, vegetation planting, invasive species removal, and wildlife water source installation vary depending on the cost of material and size of the project. A quote will be provided upon request.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Affiliates Section */}
        <div className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Affiliates</h2>
              <div className="w-24 h-1 bg-blue-600 mx-auto mb-6"></div>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Proud partners with leading organizations in conservation and land management
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {/* Placeholder for affiliate logos - replace with actual logos/links */}
              <div className="bg-gray-50 p-8 rounded-lg flex items-center justify-center hover:shadow-lg transition-shadow">
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-300 mb-2">LOGO</div>
                  <div className="text-sm text-gray-600">Partner Name</div>
                </div>
              </div>
              
              <div className="bg-gray-50 p-8 rounded-lg flex items-center justify-center hover:shadow-lg transition-shadow">
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-300 mb-2">LOGO</div>
                  <div className="text-sm text-gray-600">Partner Name</div>
                </div>
              </div>
              
              <div className="bg-gray-50 p-8 rounded-lg flex items-center justify-center hover:shadow-lg transition-shadow">
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-300 mb-2">LOGO</div>
                  <div className="text-sm text-gray-600">Partner Name</div>
                </div>
              </div>
              
              <div className="bg-gray-50 p-8 rounded-lg flex items-center justify-center hover:shadow-lg transition-shadow">
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-300 mb-2">LOGO</div>
                  <div className="text-sm text-gray-600">Partner Name</div>
                </div>
              </div>

              <div className="bg-gray-50 p-8 rounded-lg flex items-center justify-center hover:shadow-lg transition-shadow">
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-300 mb-2">LOGO</div>
                  <div className="text-sm text-gray-600">Partner Name</div>
                </div>
              </div>
              
              <div className="bg-gray-50 p-8 rounded-lg flex items-center justify-center hover:shadow-lg transition-shadow">
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-300 mb-2">LOGO</div>
                  <div className="text-sm text-gray-600">Partner Name</div>
                </div>
              </div>
              
              <div className="bg-gray-50 p-8 rounded-lg flex items-center justify-center hover:shadow-lg transition-shadow">
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-300 mb-2">LOGO</div>
                  <div className="text-sm text-gray-600">Partner Name</div>
                </div>
              </div>
              
              <div className="bg-gray-50 p-8 rounded-lg flex items-center justify-center hover:shadow-lg transition-shadow">
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-300 mb-2">LOGO</div>
                  <div className="text-sm text-gray-600">Partner Name</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="relative bg-gray-900 text-white py-12">
          <div className="absolute inset-0 bg-cover bg-center" style={{backgroundImage: 'url(/footer-background.jpg)'}}></div>
          <div className="absolute inset-0 bg-gray-900 opacity-80"></div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-2xl font-bold mb-4">CBLUE</h3>
                <p className="text-gray-400">
                  Professional land and property management services dedicated to preserving natural beauty and ecological health.
                </p>
              </div>
              <div>
                <h4 className="text-lg font-semibold mb-4">Contact</h4>
                <p className="text-gray-400 mb-2">Email: info@cblue.com</p>
                <p className="text-gray-400">Phone: (555) 123-4567</p>
              </div>
            </div>
            <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
              <p>&copy; 2024 CBLUE. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  // Main App Layout
  return (
    <div className="min-h-screen relative">
      <div className="absolute inset-0 bg-cover bg-center opacity-50" style={{backgroundImage: 'url(/background.jpg)'}}></div>
      <div className="relative z-10">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex justify-between items-center">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <img src="/cblue-logo-small.png" alt="CBLUE" className="w-10 h-10 sm:w-12 sm:h-12" />
              <div>
                <h1 className="text-base sm:text-xl font-bold text-blue-600">CBLUE Portal</h1>
                <p className="text-xs text-gray-600 hidden sm:block">
                  {userRole === 'admin' ? 'Admin Dashboard' : 'My Properties'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              <span className="text-xs sm:text-sm text-gray-600 hidden md:inline truncate max-w-[150px]">{currentUser.email}</span>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-1 text-gray-600 hover:text-gray-800 p-2"
              >
                <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-xs sm:text-sm hidden sm:inline">Logout</span>
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 text-gray-600 hover:text-gray-800"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </header>

        {/* Desktop Navigation */}
        <nav className="bg-white border-b border-gray-200 hidden lg:block">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex space-x-8">
              {navItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveView(item.id);
                    setSelectedProperty(null);
                  }}
                  className={`py-4 px-2 border-b-2 text-sm font-medium transition ${
                    activeView === item.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </nav>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="lg:hidden bg-white border-b border-gray-200 shadow-lg">
            <div className="px-4 py-2 space-y-1">
              {navItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveView(item.id);
                    setSelectedProperty(null);
                    setMobileMenuOpen(false);
                  }}
                  className={`w-full text-left px-4 py-3 rounded-md text-sm font-medium transition ${
                    activeView === item.id
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </nav>
        )}

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
{/* ADMIN VIEWS */}
          {userRole === 'admin' && (
            <>
              {/* Admin Dashboard */}
              {activeView === 'dashboard' && (
                <div className="space-y-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Dashboard Overview</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-500 text-sm">Total Properties</p>
                          <p className="text-2xl sm:text-3xl font-bold text-gray-800">{properties.length}</p>
                        </div>
                        <Users className="w-8 h-8 sm:w-10 sm:h-10 text-blue-600" />
                      </div>
                    </div>
                    <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-500 text-sm">Pending Requests</p>
                          <p className="text-2xl sm:text-3xl font-bold text-gray-800">
                            {workRequests.filter(wr => wr.status === 'pending').length}
                          </p>
                        </div>
                        <MessageSquare className="w-8 h-8 sm:w-10 sm:h-10 text-green-600" />
                      </div>
                    </div>
                    <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-500 text-sm">Total Observations</p>
                          <p className="text-2xl sm:text-3xl font-bold text-gray-800">{observations.length}</p>
                        </div>
                        <Camera className="w-8 h-8 sm:w-10 sm:h-10 text-purple-600" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow p-4 sm:p-6">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Recent Activity</h3>
                    <div className="space-y-3">
                      {observations.slice(0, 3).map(obs => (
                        <div key={obs.id} className="flex items-start space-x-3 p-3 bg-purple-50 rounded">
                          <Camera className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{obs.userEmail} submitted observation</p>
                            <p className="text-xs text-gray-600 truncate">{obs.species} at {obs.location}</p>
                          </div>
                          <span className="text-xs text-gray-500 flex-shrink-0">{formatDate(obs.createdAt)}</span>
                        </div>
                      ))}
                      {workRequests.slice(0, 2).map(wr => (
                        <div key={wr.id} className="flex items-start space-x-3 p-3 bg-green-50 rounded">
                          <MessageSquare className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{wr.userEmail} submitted work request</p>
                            <p className="text-xs text-gray-600 truncate">{wr.description.substring(0, 50)}...</p>
                          </div>
                          <span className="text-xs text-gray-500 flex-shrink-0">{formatDate(wr.createdAt)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Admin Properties List */}
              {activeView === 'properties' && !selectedProperty && (
                <div className="space-y-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800">All Properties</h2>
                  {properties.length === 0 ? (
                    <div className="bg-white rounded-lg shadow p-8 sm:p-12 text-center">
                      <Users className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500 mb-4">No properties yet</p>
                      <p className="text-sm text-gray-400">Add properties in Firestore with fields: name, acreage, location, userId, summary</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                      {properties.map(property => {
                        const coverPhoto = getCoverPhoto(property.id);
                        return (
                          <div 
                            key={property.id} 
                            onClick={() => handlePropertyClick(property)}
                            className="bg-white rounded-lg shadow hover:shadow-lg transition cursor-pointer overflow-hidden"
                          >
                            {coverPhoto && (
                              <img 
                                src={coverPhoto} 
                                alt={property.name}
                                className="w-full h-48 object-cover"
                              />
                            )}
                            <div className="p-4 sm:p-6">
                              <div className="flex justify-between items-start mb-2">
                                <h3 className="text-lg sm:text-xl font-semibold text-gray-800">{property.name}</h3>
                                <Eye className="w-5 h-5 text-blue-600 flex-shrink-0 ml-2" />
                              </div>
                              <p className="text-sm text-gray-600 mb-2">{property.acreage} acres • {property.location}</p>
                              {property.summary && (
                                <p className="text-sm text-gray-700 mt-2 line-clamp-2">{property.summary}</p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Property Detail Page */}
               {/* Property Detail Page */}
              {activeView === 'propertyDetail' && selectedProperty && (
                <div className="space-y-6">
                  <button 
                    onClick={() => {
                      setSelectedProperty(null);
                      setActiveView('properties');
                    }}
                    className="flex items-center text-blue-600 hover:text-blue-700 p-2 -ml-2"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    <span className="text-sm sm:text-base">Back to Properties</span>
                  </button>

                  {/* 1. PROPERTY INFO */}
                  <div className="bg-white rounded-lg shadow p-4 sm:p-6">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">{selectedProperty.name}</h2>
                    <p className="text-sm sm:text-base text-gray-600 mb-4">{selectedProperty.acreage} acres • {selectedProperty.location}</p>
                    {selectedProperty.summary && (
                      <p className="text-sm sm:text-base text-gray-700 bg-gray-50 p-3 sm:p-4 rounded">{selectedProperty.summary}</p>
                    )}
                  </div>

                  {/* 2. PHOTO GALLERY SECTION */}
                  <div className="bg-white rounded-lg shadow p-4 sm:p-6">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <ImageIcon className="w-5 h-5 mr-2 text-blue-600" />
                      Photo Gallery
                    </h3>
                    
                    {/* Upload Form */}
                    <form onSubmit={handleUploadPropertyPhoto} className="mb-6 p-4 bg-gray-50 rounded-lg">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Upload Photo</label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handlePhotoUploadChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Caption (optional)</label>
                          <input
                            type="text"
                            value={photoUploadForm.caption}
                            onChange={(e) => setPhotoUploadForm({...photoUploadForm, caption: e.target.value})}
                            placeholder="Photo description..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={photoUploadForm.isCover}
                            onChange={(e) => setPhotoUploadForm({...photoUploadForm, isCover: e.target.checked})}
                            className="rounded"
                          />
                          <span className="text-sm text-gray-700">Set as cover photo</span>
                        </label>
                        <button
                          type="submit"
                          onClick={() => setPhotoUploadForm({...photoUploadForm, propertyId: selectedProperty.id})}
                          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm flex items-center space-x-1"
                        >
                          <Plus className="w-4 h-4" />
                          <span>Upload Photo</span>
                        </button>
                      </div>
                    </form>

                    {/* Photo Grid */}
                    {getPropertyPhotos(selectedProperty.id).length === 0 ? (
                      <p className="text-gray-500 text-center py-8 text-sm">No photos yet for this property</p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {getPropertyPhotos(selectedProperty.id).map(photo => (
                          <div key={photo.id} className="relative group">
                            <img 
                              src={photo.photoURL} 
                              alt={photo.caption || 'Property photo'}
                              className="w-full h-48 object-cover rounded-lg cursor-pointer"
                              onClick={() => setSelectedImage(photo.photoURL)}
                            />
                            {photo.isCover && (
                              <div className="absolute top-2 left-2 bg-blue-600 text-white px-2 py-1 rounded text-xs font-medium">
                                Cover Photo
                              </div>
                            )}
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100">
                              <div className="flex space-x-2">
                                {!photo.isCover && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSetCoverPhoto(photo.id);
                                    }}
                                    className="bg-white text-gray-800 px-3 py-1 rounded text-xs font-medium hover:bg-gray-100"
                                  >
                                    Set as Cover
                                  </button>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeletePropertyPhoto(photo.id, photo.photoURL);
                                  }}
                                  className="bg-red-600 text-white px-3 py-1 rounded text-xs font-medium hover:bg-red-700"
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                            {photo.caption && (
                              <p className="text-sm text-gray-700 mt-2">{photo.caption}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* 3. REPORTS SECTION */}
                  <div className="bg-white rounded-lg shadow p-4 sm:p-6">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <FileText className="w-5 h-5 mr-2 text-blue-600" />
                      Reports & Documents
                    </h3>
                    {reports.filter(r => r.propertyId === selectedProperty.id).length === 0 ? (
                      <p className="text-gray-500 text-center py-8 text-sm">No reports yet for this property</p>
                    ) : (
                      <div className="space-y-3">
                        {reports.filter(r => r.propertyId === selectedProperty.id).map(report => (
                          <div key={report.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 bg-gray-50 rounded hover:bg-gray-100 gap-3">
                            <div className="flex items-center space-x-3 min-w-0 flex-1">
                              <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                              <div className="min-w-0">
                                <p className="font-medium text-gray-800 truncate">{report.title}</p>
                                <p className="text-xs text-gray-500">{report.date}</p>
                              </div>
                            </div>
                            <a 
                              href={report.fileURL} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center justify-center space-x-2 text-blue-600 hover:text-blue-700 px-4 py-2 bg-white rounded border border-blue-600 hover:bg-blue-50 flex-shrink-0"
                            >
                              <Download className="w-4 h-4" />
                              <span className="text-sm font-medium">Download</span>
                            </a>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* 4. WORK REQUESTS SECTION */}
                  <div className="bg-white rounded-lg shadow p-4 sm:p-6">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <MessageSquare className="w-5 h-5 mr-2 text-green-600" />
                      Work Requests for this Property
                    </h3>
                    {workRequests.filter(wr => wr.propertyId === selectedProperty.id).length === 0 ? (
                      <p className="text-gray-500 text-center py-8 text-sm">No work requests for this property</p>
                    ) : (
                      <div className="space-y-4">
                        {workRequests.filter(wr => wr.propertyId === selectedProperty.id).map(request => (
                          <div key={request.id} className="border rounded-lg p-4">
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-3">
                              <div className="flex-1">
                                <p className="font-medium text-gray-800">{request.area}</p>
                                <p className="text-sm text-gray-600">Submitted: {formatDate(request.createdAt)}</p>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  request.urgency === 'High' ? 'bg-red-100 text-red-700' :
                                  request.urgency === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-green-100 text-green-700'
                                }`}>
                                  {request.urgency}
                                </span>
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  request.status === 'completed' ? 'bg-green-100 text-green-700' :
                                  request.status === 'in-progress' ? 'bg-blue-100 text-blue-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {request.status === 'completed' ? 'Completed' :
                                   request.status === 'in-progress' ? 'In Progress' :
                                   'Pending'}
                                </span>
                              </div>
                            </div>
                            <p className="text-sm text-gray-700 mb-2">{request.description}</p>
                            <p className="text-xs text-gray-500">Timeline: {request.timeline}</p>
                            
                            {request.status === 'completed' && request.completionSummary && (
                              <div className="mt-3 p-3 bg-green-50 rounded">
                                <p className="text-sm font-medium text-gray-800">Work Completed:</p>
                                <p className="text-sm text-gray-700 mt-1">{request.completionSummary}</p>
                                {request.completionDate && (
                                  <p className="text-xs text-gray-500 mt-1">Completed on: {request.completionDate}</p>
                                )}
                              </div>
                            )}

                            {editingRequest === request.id ? (
                              <div className="mt-3 p-3 bg-gray-50 rounded space-y-3">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Completion Summary</label>
                                  <textarea
                                    value={completionForm.summary}
                                    onChange={(e) => setCompletionForm({...completionForm, summary: e.target.value})}
                                    rows={3}
                                    placeholder="Describe the work that was completed..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Completion Date</label>
                                  <input
                                    type="date"
                                    value={completionForm.completionDate}
                                    onChange={(e) => setCompletionForm({...completionForm, completionDate: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                  />
                                </div>
                                <div className="flex flex-col sm:flex-row gap-2">
                                  <button
                                    onClick={() => handleSaveCompletionSummary(request.id)}
                                    className="text-sm bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                                  >
                                    Save & Mark Complete
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingRequest(null);
                                      setCompletionForm({ summary: '', completionDate: '' });
                                    }}
                                    className="text-sm bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : request.status !== 'completed' && (
                              <div className="mt-3 flex flex-col sm:flex-row gap-2">
                                {request.status === 'pending' && (
                                  <button
                                    onClick={() => handleUpdateWorkRequestStatus(request.id, 'in-progress')}
                                    className="text-sm text-blue-600 hover:text-blue-700 flex items-center justify-center space-x-1 p-2"
                                  >
                                    <Clock className="w-4 h-4" />
                                    <span>Mark In Progress</span>
                                  </button>
                                )}
                                <button
                                  onClick={() => setEditingRequest(request.id)}
                                  className="text-sm text-green-600 hover:text-green-700 flex items-center justify-center space-x-1 p-2"
                                >
                                  <Edit className="w-4 h-4" />
                                  <span>Add Completion Summary</span>
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* 5. OBSERVATIONS SECTION (NOW AT BOTTOM) */}
                  <div className="bg-white rounded-lg shadow p-4 sm:p-6">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <Camera className="w-5 h-5 mr-2 text-purple-600" />
                      Observations for this Property
                    </h3>
                    {observations.filter(obs => obs.propertyId === selectedProperty.id).length === 0 ? (
                      <p className="text-gray-500 text-center py-8 text-sm">No observations yet for this property</p>
                    ) : (
                      <div className="space-y-4">
                        {observations.filter(obs => obs.propertyId === selectedProperty.id).map(obs => (
                          <div key={obs.id} className="border rounded-lg p-4">
                            <div className="flex flex-col sm:flex-row sm:items-start space-y-3 sm:space-y-0 sm:space-x-4">
                              <div className="p-3 bg-purple-100 rounded-lg self-start">
                                <Camera className="w-6 h-6 text-purple-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-2">
                                  <div className="min-w-0">
                                    <h4 className="font-semibold text-gray-800">{obs.species || 'Unidentified Species'}</h4>
                                    <p className="text-sm text-gray-600">{obs.userEmail}</p>
                                  </div>
                                  <button
                                    onClick={() => handleDeleteObservation(obs.id)}
                                    className="text-red-600 hover:text-red-700 p-2 self-end sm:self-start"
                                    title="Delete observation"
                                  >
                                    <Trash2 className="w-5 h-5" />
                                  </button>
                                </div>
                                <p className="text-sm text-gray-700"><strong>Location:</strong> {obs.location}</p>
                                {obs.notes && <p className="text-sm text-gray-700"><strong>Notes:</strong> {obs.notes}</p>}
                                <p className="text-xs text-gray-500 mt-2">Date: {formatDate(obs.createdAt)}</p>
                                {obs.photoURL && (
                                  <img 
                                    src={obs.photoURL} 
                                    alt="Observation" 
                                    className="mt-3 rounded-lg max-w-full sm:max-w-md w-full object-cover cursor-pointer"
                                    onClick={() => setSelectedImage(obs.photoURL)}
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {/* Admin Updates View */}
              {activeView === 'updates' && (
                <div className="space-y-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Post Updates</h2>
                  <div className="bg-white rounded-lg shadow p-4 sm:p-6">
                    <form onSubmit={handlePostUpdate} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Property</label>
                        <select
                          value={updateForm.propertyId}
                          onChange={(e) => setUpdateForm({...updateForm, propertyId: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                          required
                        >
                          <option value="">Select property...</option>
                          <option value="all">All Clients (Broadcast)</option>
                          {properties.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                        <input
                          type="text"
                          value={updateForm.title}
                          onChange={(e) => setUpdateForm({...updateForm, title: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                        <textarea
                          value={updateForm.content}
                          onChange={(e) => setUpdateForm({...updateForm, content: e.target.value})}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Attach PDF (optional)</label>
                          <input
                            type="file"
                            accept=".pdf"
                            onChange={handlePDFChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                          {updateForm.pdfFile && (
                            <p className="text-xs text-gray-600 mt-1">📄 {updateForm.pdfFile.name}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Attach Image (optional)</label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                          {updateForm.imageFile && (
                            <p className="text-xs text-gray-600 mt-1">🖼️ {updateForm.imageFile.name}</p>
                          )}
                        </div>
                      </div>
                      <button
                        type="submit"
                        className="w-full sm:w-auto bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 text-base font-medium"
                      >
                        Post Update
                      </button>
                    </form>
                  </div>

                  <div className="bg-white rounded-lg shadow p-4 sm:p-6">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Recent Updates</h3>
                    {updates.length === 0 ? (
                      <p className="text-gray-500 text-center py-8 text-sm">No updates yet</p>
                    ) : (
                      <div className="space-y-3">
                        {updates.map(update => (
                          <div key={update.id} className="border-l-4 border-blue-500 pl-4 py-2 relative">
                            <button
                              onClick={() => handleDeleteUpdate(update.id, update.pdfURL, update.imageURL)}
                              className="absolute top-2 right-2 text-red-600 hover:text-red-700 p-2"
                              title="Delete update"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                            <p className="font-medium text-gray-800 pr-10">{update.title}</p>
                            <p className="text-sm text-gray-600 mt-1">{update.content}</p>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {update.pdfURL && (
                                <a 
                                  href={update.pdfURL} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center space-x-1 text-blue-600 hover:text-blue-700 text-sm"
                                >
                                  <FileText className="w-4 h-4" />
                                  <span>{update.pdfName || 'View PDF'}</span>
                                </a>
                              )}
                              {update.imageURL && (
                                <a 
                                  href={update.imageURL} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center space-x-1 text-blue-600 hover:text-blue-700 text-sm"
                                >
                                  <Camera className="w-4 h-4" />
                                  <span>{update.imageName || 'View Image'}</span>
                                </a>
                              )}
                            </div>
                            <div className="flex items-center space-x-3 mt-2">
                              <span className="text-xs text-gray-500">{formatDate(update.createdAt)}</span>
                              <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                                {update.propertyId === 'all' ? 'All Clients' : 'Specific Property'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Admin Work Requests View */}
              {activeView === 'workRequests' && (
                <div className="space-y-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Work Requests</h2>
                  {workRequests.length === 0 ? (
                    <div className="bg-white rounded-lg shadow p-8 sm:p-12 text-center">
                      <MessageSquare className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No work requests yet</p>
                    </div>
                  ) : (
                    workRequests.map(request => (
                      <div key={request.id} className="bg-white rounded-lg shadow p-4 sm:p-6">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-3">
                          <div className="flex-1">
                            <h3 className="text-base sm:text-lg font-semibold text-gray-800">{request.userEmail}</h3>
                            <p className="text-sm text-gray-600">{request.propertyName} • {request.area}</p>
                            <p className="text-xs text-gray-500 mt-1">Submitted: {formatDate(request.createdAt)}</p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              request.urgency === 'High' ? 'bg-red-100 text-red-700' :
                              request.urgency === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {request.urgency}
                            </span>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              request.status === 'completed' ? 'bg-green-100 text-green-700' :
                              request.status === 'in-progress' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {request.status === 'completed' ? 'Completed' :
                               request.status === 'in-progress' ? 'In Progress' :
                               'Pending'}
                            </span>
                          </div>
                        </div>
                        <p className="text-sm sm:text-base text-gray-700 mb-3">{request.description}</p>
                        <div className="text-sm text-gray-600 mb-3">
                          <span>Timeline: {request.timeline}</span>
                        </div>

                        {request.status === 'completed' && request.completionSummary && (
                          <div className="p-3 bg-green-50 rounded mb-3">
                            <p className="text-sm font-medium text-gray-800">Work Completed:</p>
                            <p className="text-sm text-gray-700 mt-1">{request.completionSummary}</p>
                            {request.completionDate && (
                              <p className="text-xs text-gray-500 mt-1">Completed on: {request.completionDate}</p>
                            )}
                          </div>
                        )}

                        {request.status !== 'completed' && (
                          <div className="flex flex-col sm:flex-row gap-2">
                            {request.status === 'pending' && (
                              <button 
                                onClick={() => handleUpdateWorkRequestStatus(request.id, 'in-progress')}
                                className="flex items-center justify-center space-x-1 text-blue-600 hover:text-blue-700 font-medium text-sm p-2"
                              >
                                <Clock className="w-4 h-4" />
                                <span>Mark In Progress</span>
                              </button>
                            )}
                            <button 
                              onClick={() => handleUpdateWorkRequestStatus(request.id, 'completed')}
                              className="flex items-center justify-center space-x-1 text-green-600 hover:text-green-700 font-medium text-sm p-2"
                            >
                              <CheckCircle className="w-4 h-4" />
                              <span>Mark Complete</span>
                            </button>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Admin All Observations View */}
              {activeView === 'observations' && (
                <div className="space-y-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800">All Wildlife & Plant Observations</h2>
                  {observations.length === 0 ? (
                    <div className="bg-white rounded-lg shadow p-8 sm:p-12 text-center">
                      <Camera className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No observations yet</p>
                    </div>
                  ) : (
                    observations.map(obs => (
                      <div key={obs.id} className="bg-white rounded-lg shadow p-4 sm:p-6">
                        <div className="flex flex-col sm:flex-row sm:items-start space-y-3 sm:space-y-0 sm:space-x-4">
                          <div className="p-3 bg-purple-100 rounded-lg self-start">
                            <Camera className="w-6 h-6 text-purple-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-2">
                              <div className="min-w-0">
                                <h3 className="font-semibold text-gray-800">{obs.species || 'Unidentified Species'}</h3>
                                <p className="text-sm text-gray-600">{obs.userEmail} • {obs.propertyName}</p>
                              </div>
                              <button
                                onClick={() => handleDeleteObservation(obs.id)}
                                className="text-red-600 hover:text-red-700 p-2 self-end sm:self-start"
                                title="Delete observation"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                            <p className="text-sm text-gray-700"><strong>Location:</strong> {obs.location}</p>
                            {obs.notes && <p className="text-sm text-gray-700"><strong>Notes:</strong> {obs.notes}</p>}
                            <p className="text-xs text-gray-500 mt-2">Date: {formatDate(obs.createdAt)}</p>
                            {obs.photoURL && (
                              <img 
                                src={obs.photoURL} 
                                alt="Observation" 
                                className="mt-3 rounded-lg max-w-full sm:max-w-md w-full object-cover cursor-pointer"
                                onClick={() => setSelectedImage(obs.photoURL)}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
{/* Admin Resources View */}
              {activeView === 'resources' && (
                <div className="space-y-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Resources</h2>
                  
                  {/* Add Resource Form */}
                  <div className="bg-white rounded-lg shadow p-4 sm:p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Add New Resource</h3>
                    <form onSubmit={handleSubmitResource} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                        <input
                          type="text"
                          value={resourceForm.title}
                          onChange={(e) => setResourceForm({...resourceForm, title: e.target.value})}
                          placeholder="Resource name or title"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">URL/Link *</label>
                        <input
                          type="url"
                          value={resourceForm.url}
                          onChange={(e) => setResourceForm({...resourceForm, url: e.target.value})}
                          placeholder="https://example.com"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                        <textarea
                          value={resourceForm.description}
                          onChange={(e) => setResourceForm({...resourceForm, description: e.target.value})}
                          rows={3}
                          placeholder="Brief description of this resource..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Image/Icon (optional)</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleResourceImageChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        />
                        {resourceForm.imageFile && (
                          <p className="text-xs text-gray-600 mt-1">Selected: {resourceForm.imageFile.name}</p>
                        )}
                      </div>
                      
                      <button
                        type="submit"
                        className="w-full sm:w-auto bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 text-base font-medium"
                      >
                        Add Resource
                      </button>
                    </form>
                  </div>

                  {/* Resources List */}
                  <div className="bg-white rounded-lg shadow p-4 sm:p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Current Resources</h3>
                    {resources.length === 0 ? (
                      <p className="text-gray-500 text-center py-8 text-sm">No resources yet</p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {resources.map(resource => (
                          <div key={resource.id} className="border rounded-lg p-4 hover:shadow-md transition">
                            <div className="flex items-start space-x-4">
                              {resource.imageURL && (
                                <img 
                                  src={resource.imageURL} 
                                  alt={resource.title}
                                  className="w-16 h-16 object-cover rounded flex-shrink-0"
                                />
                              )}
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-gray-800 mb-1">{resource.title}</h4>
                                {resource.description && (
                                  <p className="text-sm text-gray-600 mb-2">{resource.description}</p>
                                )}
                                <a 
                                  href={resource.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-sm text-blue-600 hover:text-blue-700 break-all"
                                >
                                  {resource.url}
                                </a>
                              </div>
                              <button
                                onClick={() => handleDeleteResource(resource.id, resource.imageURL)}
                                className="text-red-600 hover:text-red-700 p-2"
                                title="Delete resource"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* CLIENT VIEWS */}
          {userRole === 'client' && (
            <>
              {/* Client Properties List */}
              {activeView === 'properties' && !selectedProperty && (
                <div className="space-y-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800">My Properties</h2>
                  {properties.length === 0 ? (
                    <div className="bg-white rounded-lg shadow p-8 sm:p-12 text-center">
                      <Users className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No properties assigned yet</p>
                      <p className="text-sm text-gray-400 mt-2">Contact your biologist to get started</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                      {properties.map(property => {
                        const coverPhoto = getCoverPhoto(property.id);
                        return (
                          <div 
                            key={property.id} 
                            onClick={() => handlePropertyClick(property)}
                            className="bg-white rounded-lg shadow hover:shadow-lg transition cursor-pointer overflow-hidden"
                          >
                            {coverPhoto && (
                              <img 
                                src={coverPhoto} 
                                alt={property.name}
                                className="w-full h-48 object-cover"
                              />
                            )}
                            <div className="p-4 sm:p-6">
                              <div className="flex justify-between items-start mb-2">
                                <h3 className="text-lg sm:text-xl font-semibold text-gray-800">{property.name}</h3>
                                <Eye className="w-5 h-5 text-blue-600 flex-shrink-0 ml-2" />
                              </div>
                              <p className="text-sm text-gray-600 mb-2">{property.acreage} acres • {property.location}</p>
                              {property.summary && (
                                <p className="text-sm text-gray-700 mt-2 line-clamp-2">{property.summary}</p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Client Property Detail */}
             {/* Client Property Detail */}
              {activeView === 'propertyDetail' && selectedProperty && (
                <div className="space-y-6">
                  <button 
                    onClick={() => {
                      setSelectedProperty(null);
                      setActiveView('properties');
                    }}
                    className="flex items-center text-blue-600 hover:text-blue-700 p-2 -ml-2"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    <span className="text-sm sm:text-base">Back to Properties</span>
                  </button>

                  {/* 1. PROPERTY INFO */}
                  <div className="bg-white rounded-lg shadow p-4 sm:p-6">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">{selectedProperty.name}</h2>
                    <p className="text-sm sm:text-base text-gray-600 mb-4">{selectedProperty.acreage} acres • {selectedProperty.location}</p>
                    {selectedProperty.summary && (
                      <p className="text-sm sm:text-base text-gray-700 bg-gray-50 p-3 sm:p-4 rounded">{selectedProperty.summary}</p>
                    )}
                  </div>

                  {/* 2. PHOTO GALLERY SECTION */}
                  <div className="bg-white rounded-lg shadow p-4 sm:p-6">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <ImageIcon className="w-5 h-5 mr-2 text-blue-600" />
                      Photo Gallery
                    </h3>
                    {getPropertyPhotos(selectedProperty.id).length === 0 ? (
                      <p className="text-gray-500 text-center py-8 text-sm">No photos yet for this property</p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {getPropertyPhotos(selectedProperty.id).map(photo => (
                          <div key={photo.id} className="relative group">
                            <img 
                              src={photo.photoURL} 
                              alt={photo.caption || 'Property photo'}
                              className="w-full h-48 object-cover rounded-lg cursor-pointer"
                              onClick={() => setSelectedImage(photo.photoURL)}
                            />
                            {photo.caption && (
                              <p className="text-sm text-gray-700 mt-2">{photo.caption}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* 3. REPORTS SECTION */}
                  <div className="bg-white rounded-lg shadow p-4 sm:p-6">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <FileText className="w-5 h-5 mr-2 text-blue-600" />
                      Reports & Documents
                    </h3>
                    {reports.filter(r => r.propertyId === selectedProperty.id).length === 0 ? (
                      <p className="text-gray-500 text-center py-8 text-sm">No reports yet for this property</p>
                    ) : (
                      <div className="space-y-3">
                        {reports.filter(r => r.propertyId === selectedProperty.id).map(report => (
                          <div key={report.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 bg-gray-50 rounded hover:bg-gray-100 gap-3">
                            <div className="flex items-center space-x-3 min-w-0 flex-1">
                              <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                              <div className="min-w-0">
                                <p className="font-medium text-gray-800 truncate">{report.title}</p>
                                <p className="text-xs text-gray-500">{report.date}</p>
                              </div>
                            </div>
                            <a 
                              href={report.fileURL} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center justify-center space-x-2 text-blue-600 hover:text-blue-700 px-4 py-2 bg-white rounded border border-blue-600 hover:bg-blue-50 flex-shrink-0"
                            >
                              <Download className="w-4 h-4" />
                              <span className="text-sm font-medium">Download</span>
                            </a>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* 4. WORK REQUESTS SECTION */}
                  <div className="bg-white rounded-lg shadow p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center">
                        <MessageSquare className="w-5 h-5 mr-2 text-green-600" />
                        My Work Requests for this Property
                      </h3>
                      <button
                        onClick={() => {
                          setWorkRequestForm({...workRequestForm, propertyId: selectedProperty.id});
                          setActiveView('workRequest');
                        }}
                        className="flex items-center space-x-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 text-sm"
                      >
                        <Plus className="w-4 h-4" />
                        <span>New Request</span>
                      </button>
                    </div>
                    {workRequests.filter(wr => wr.propertyId === selectedProperty.id).length === 0 ? (
                      <p className="text-gray-500 text-center py-8 text-sm">No work requests for this property</p>
                    ) : (
                      <div className="space-y-4">
                        {workRequests.filter(wr => wr.propertyId === selectedProperty.id).map(request => (
                          <div key={request.id} className="border rounded-lg p-4">
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-2">
                              <p className="font-medium text-gray-800">{request.area}</p>
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                request.status === 'completed' ? 'bg-green-100 text-green-700' :
                                request.status === 'in-progress' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {request.status === 'completed' ? 'Completed' :
                                 request.status === 'in-progress' ? 'In Progress' :
                                 'Pending'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 mb-2">{request.description}</p>
                            <p className="text-xs text-gray-500">Submitted: {formatDate(request.createdAt)}</p>
                            
                            {request.status === 'completed' && request.completionSummary && (
                              <div className="mt-3 p-3 bg-green-50 rounded">
                                <p className="text-sm font-medium text-gray-800">Work Completed:</p>
                                <p className="text-sm text-gray-700 mt-1">{request.completionSummary}</p>
                                {request.completionDate && (
                                  <p className="text-xs text-gray-500 mt-1">Completed on: {request.completionDate}</p>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* 5. OBSERVATIONS SECTION (NOW AT BOTTOM) */}
                  <div className="bg-white rounded-lg shadow p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center">
                        <Camera className="w-5 h-5 mr-2 text-purple-600" />
                        My Observations for this Property
                      </h3>
                      <button
                        onClick={() => {
                          setObservationForm({...observationForm, propertyId: selectedProperty.id});
                          setActiveView('submitObservation');
                        }}
                        className="flex items-center space-x-1 bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 text-sm"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Add Observation</span>
                      </button>
                    </div>
                    {observations.filter(obs => obs.propertyId === selectedProperty.id).length === 0 ? (
                      <p className="text-gray-500 text-center py-8 text-sm">No observations yet for this property</p>
                    ) : (
                      <div className="space-y-4">
                        {observations.filter(obs => obs.propertyId === selectedProperty.id).map(obs => (
                          <div key={obs.id} className="border rounded-lg p-4">
                            <div className="flex flex-col sm:flex-row sm:items-start space-y-3 sm:space-y-0 sm:space-x-4">
                              <div className="p-3 bg-purple-100 rounded-lg self-start">
                                <Camera className="w-6 h-6 text-purple-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-2">
                                  <h4 className="font-semibold text-gray-800">{obs.species || 'Unidentified Species'}</h4>
                                  <button
                                    onClick={() => handleDeleteObservation(obs.id)}
                                    className="text-red-600 hover:text-red-700 p-2 self-end sm:self-start"
                                    title="Delete observation"
                                  >
                                    <Trash2 className="w-5 h-5" />
                                  </button>
                                </div>
                                <p className="text-sm text-gray-700"><strong>Location:</strong> {obs.location}</p>
                                {obs.notes && <p className="text-sm text-gray-700"><strong>Notes:</strong> {obs.notes}</p>}
                                <p className="text-xs text-gray-500 mt-2">Date: {formatDate(obs.createdAt)}</p>
                                {obs.photoURL && (
                                  <img 
                                    src={obs.photoURL} 
                                    alt="Observation" 
                                    className="mt-3 rounded-lg max-w-full sm:max-w-md w-full object-cover cursor-pointer"
                                    onClick={() => setSelectedImage(obs.photoURL)}
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {/* Client Updates View */}
              {activeView === 'updates' && (
                <div className="space-y-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Updates & News</h2>
                  {updates.length === 0 ? (
                    <div className="bg-white rounded-lg shadow p-8 sm:p-12 text-center">
                      <Bell className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No updates yet</p>
                    </div>
                  ) : (
                    updates.map(update => (
                      <div key={update.id} className="bg-white rounded-lg shadow p-4 sm:p-6 border-l-4 border-blue-500">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-2">
                          <h3 className="text-base sm:text-lg font-semibold text-gray-800">{update.title}</h3>
                          <span className="text-xs sm:text-sm text-gray-500 flex-shrink-0">{formatDate(update.createdAt)}</span>
                        </div>
                        <p className="text-sm sm:text-base text-gray-700">{update.content}</p>
                        {update.imageURL && (
                          <img 
                            src={update.imageURL} 
                            alt="Update" 
                            className="mt-3 rounded-lg max-w-full sm:max-w-md w-full object-cover cursor-pointer"
                            onClick={() => setSelectedImage(update.imageURL)}
                          />
                        )}
                        <div className="flex flex-wrap gap-2 mt-3">
                          {update.pdfURL && (
                            <a 
                              href={update.pdfURL} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="inline-flex items-center space-x-1 text-blue-600 hover:text-blue-700 p-2 bg-blue-50 rounded"
                            >
                              <FileText className="w-4 h-4" />
                              <span className="text-sm font-medium">{update.pdfName || 'View PDF'}</span>
                            </a>
                          )}
                          {update.imageURL && (
                            <a 
                              href={update.imageURL} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="inline-flex items-center space-x-1 text-blue-600 hover:text-blue-700 p-2 bg-blue-50 rounded"
                            >
                              <Download className="w-4 h-4" />
                              <span className="text-sm">Download Image</span>
                            </a>
                          )}
                        </div>
                        {update.propertyId === 'all' && (
                          <div className="mt-3">
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">All Properties</span>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
{/* Client Resources View */}
              {activeView === 'resources' && (
                <div className="space-y-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Resources</h2>
                  {resources.length === 0 ? (
                    <div className="bg-white rounded-lg shadow p-8 sm:p-12 text-center">
                      <FileText className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No resources available yet</p>
                    </div>
                  ) : (
<div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                      {resources.map(resource => (
                        <a
                          key={resource.id}
                          href={resource.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-white rounded-lg shadow p-4 sm:p-6 hover:shadow-lg transition block"
                        >
                          <div className="flex items-start space-x-4">
                            {resource.imageURL && (
                              <img 
                                src={resource.imageURL} 
                                alt={resource.title}
                                className="w-20 h-20 object-cover rounded flex-shrink-0"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <h3 className="text-lg font-semibold text-gray-800 mb-2">{resource.title}</h3>
                              {resource.description && (
                                <p className="text-sm text-gray-600 mb-2">{resource.description}</p>
                              )}
                              <p className="text-xs text-blue-600 hover:text-blue-700 flex items-center space-x-1">
                                <span>Visit Resource</span>
                                <Eye className="w-4 h-4" />
                              </p>
                            </div>
                          </div>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {/* Client My Observations View */}
              {activeView === 'observations' && (
                <div className="space-y-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800">My Observations</h2>
                  {observations.length === 0 ? (
                    <div className="bg-white rounded-lg shadow p-8 sm:p-12 text-center">
                      <Camera className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No observations yet</p>
                    </div>
                  ) : (
                    observations.map(obs => (
                      <div key={obs.id} className="bg-white rounded-lg shadow p-4 sm:p-6">
                        <div className="flex flex-col sm:flex-row sm:items-start space-y-3 sm:space-y-0 sm:space-x-4">
                          <div className="p-3 bg-purple-100 rounded-lg self-start">
                            <Camera className="w-6 h-6 text-purple-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-2">
                              <div className="min-w-0">
                                <h3 className="font-semibold text-gray-800">{obs.species || 'Unidentified Species'}</h3>
                                <p className="text-sm text-gray-600">{obs.propertyName}</p>
                              </div>
                              <button
                                onClick={() => handleDeleteObservation(obs.id)}
                                className="text-red-600 hover:text-red-700 p-2 self-end sm:self-start"
                                title="Delete observation"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                            <p className="text-sm text-gray-700"><strong>Location:</strong> {obs.location}</p>
                            {obs.notes && <p className="text-sm text-gray-700"><strong>Notes:</strong> {obs.notes}</p>}
                            <p className="text-xs text-gray-500 mt-2">Date: {formatDate(obs.createdAt)}</p>
                            {obs.photoURL && (
                              <img 
                                src={obs.photoURL} 
                                alt="Observation" 
                                className="mt-3 rounded-lg max-w-full sm:max-w-md w-full object-cover cursor-pointer"
                                onClick={() => setSelectedImage(obs.photoURL)}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Submit Observation Form */}
              {activeView === 'submitObservation' && (
                <div className="space-y-6">
                  <button 
                    onClick={() => {
                      if (selectedProperty) {
                        setActiveView('propertyDetail');
                      } else {
                        setActiveView('observations');
                      }
                    }}
                    className="text-blue-600 hover:text-blue-700 flex items-center space-x-1 p-2 -ml-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span className="text-sm sm:text-base">Back</span>
                  </button>
                  
                  <div className="bg-white rounded-lg shadow p-4 sm:p-6">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-6">Submit Observation</h2>
                    <form onSubmit={handleSubmitObservation} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Property *</label>
                        <select
                          value={observationForm.propertyId}
                          onChange={(e) => setObservationForm({...observationForm, propertyId: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                          required
                        >
                          <option value="">Select property...</option>
                          {properties.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Species (optional)</label>
                        <input
                          type="text"
                          value={observationForm.species}
                          onChange={(e) => setObservationForm({...observationForm, species: e.target.value})}
                          placeholder="e.g., White-tailed Deer, Red Oak"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Location on Property *</label>
                        <input
                          type="text"
                          value={observationForm.location}
                          onChange={(e) => setObservationForm({...observationForm, location: e.target.value})}
                          placeholder="e.g., Northern boundary, South pasture"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                        <textarea
                          value={observationForm.notes}
                          onChange={(e) => setObservationForm({...observationForm, notes: e.target.value})}
                          rows={3}
                          placeholder="Additional details..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Photo (optional)</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                        />
                        {observationForm.photo && (
                          <p className="text-sm text-gray-600 mt-1">Selected: {observationForm.photo.name}</p>
                        )}
                      </div>
                      
                      <button
                        type="submit"
                        className="w-full sm:w-auto bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 text-base font-medium"
                      >
                        Submit Observation
                      </button>
                    </form>
                  </div>
                </div>
              )}

              {/* Submit Work Request Form */}
              {activeView === 'workRequest' && (
                <div className="space-y-6">
                  <div className="bg-white rounded-lg shadow p-4 sm:p-6">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-6">Submit Work Request</h2>
                    <form onSubmit={handleSubmitWorkRequest} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Property *</label>
                        <select
                          value={workRequestForm.propertyId}
                          onChange={(e) => setWorkRequestForm({...workRequestForm, propertyId: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                          required
                        >
                          <option value="">Select property...</option>
                          {properties.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Urgency *</label>
                        <select
                          value={workRequestForm.urgency}
                          onChange={(e) => setWorkRequestForm({...workRequestForm, urgency: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                        >
                          <option value="Low">Low</option>
                          <option value="Medium">Medium</option>
                          <option value="High">High</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Area/Location *</label>
                        <input
                          type="text"
                          value={workRequestForm.area}
                          onChange={(e) => setWorkRequestForm({...workRequestForm, area: e.target.value})}
                          placeholder="e.g., South Pasture, Northern Woods"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                        <textarea
                          value={workRequestForm.description}
                          onChange={(e) => setWorkRequestForm({...workRequestForm, description: e.target.value})}
                          rows={4}
                          placeholder="Describe the work needed..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Timeline *</label>
                        <input
                          type="text"
                          value={workRequestForm.timeline}
                          onChange={(e) => setWorkRequestForm({...workRequestForm, timeline: e.target.value})}
                          placeholder="e.g., Next 2 weeks, Before spring"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                          required
                        />
                      </div>
                      
                      <button
                        type="submit"
                        className="w-full sm:w-auto bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 text-base font-medium"
                      >
                        Submit Request
                      </button>
                    </form>
                  </div>
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 text-2xl font-bold"
            >
              ×
            </button>
            <img 
              src={selectedImage} 
              alt="Full size" 
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default App;